import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const FloatingCircuits = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const ctx = canvas?.getContext('2d');
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize nodes
    const initNodes = () => {
      nodesRef.current = [];
      const nodeCount = Math.floor((canvas?.width * canvas?.height) / 15000);
      
      for (let i = 0; i < nodeCount; i++) {
        nodesRef?.current?.push({
          x: Math.random() * canvas?.width,
          y: Math.random() * canvas?.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: Math.random() * 2 + 1,
          opacity: Math.random() * 0.5 + 0.2,
          pulsePhase: Math.random() * Math.PI * 2
        });
      }
    };

    initNodes();

    // Mouse tracking
    const handleMouseMove = (e) => {
      const rect = canvas?.getBoundingClientRect();
      mouseRef.current = {
        x: e?.clientX - rect?.left,
        y: e?.clientY - rect?.top
      };
    };

    canvas?.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    const animate = () => {
      ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
      
      const time = Date.now() * 0.001;
      const nodes = nodesRef?.current;

      // Update and draw nodes
      nodes?.forEach((node, i) => {
        // Update position
        node.x += node?.vx;
        node.y += node?.vy;

        // Bounce off edges
        if (node?.x <= 0 || node?.x >= canvas?.width) node.vx *= -1;
        if (node?.y <= 0 || node?.y >= canvas?.height) node.vy *= -1;

        // Mouse interaction
        const dx = mouseRef?.current?.x - node?.x;
        const dy = mouseRef?.current?.y - node?.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 150) {
          const force = (150 - distance) / 150;
          node.x -= (dx / distance) * force * 2;
          node.y -= (dy / distance) * force * 2;
        }

        // Pulsing effect
        const pulseOpacity = node?.opacity + Math.sin(time * 2 + node?.pulsePhase) * 0.2;

        // Draw node
        ctx?.beginPath();
        ctx?.arc(node?.x, node?.y, node?.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(246, 229, 92, ${pulseOpacity})`;
        ctx?.fill();

        // Draw connections
        nodes?.slice(i + 1)?.forEach(otherNode => {
          const dx = otherNode?.x - node?.x;
          const dy = otherNode?.y - node?.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            const opacity = (120 - distance) / 120 * 0.3;
            ctx?.beginPath();
            ctx?.moveTo(node?.x, node?.y);
            ctx?.lineTo(otherNode?.x, otherNode?.y);
            ctx.strokeStyle = `rgba(246, 229, 92, ${opacity})`;
            ctx.lineWidth = 1;
            ctx?.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas?.removeEventListener('mousemove', handleMouseMove);
      if (animationRef?.current) {
        cancelAnimationFrame(animationRef?.current);
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-0">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 opacity-20"
        style={{ mixBlendMode: 'screen' }}
      />
      {/* Additional floating elements */}
      <div className="absolute inset-0">
        {[...Array(8)]?.map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-accent rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            transition={{
              duration: Math.random() * 20 + 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear"
            }}
            style={{
              opacity: Math.random() * 0.5 + 0.2,
            }}
          />
        ))}
      </div>
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-accent/10" />
      <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-accent/5 to-transparent" />
    </div>
  );
};

export default FloatingCircuits;